/**
 * `<BrowseVersionsModal>` — Browse conversation versions + inline diff.
 *
 * conversation-model-versioning Phase 5 Session 3 (2026-05-17).
 *
 * Root-mounted ONCE at `routes/builder.tsx` as a sibling of
 * `<SendDealLinkModal>`. Listens for the `arcanum:open-versions` window event
 * (dispatched by `<ManageVersionsButton>`'s Browse item) with a conversation-id-match
 * guard inside the listener — mirrors the `<SendDealLinkModal>` pattern
 * (external-sharing Phase 3 Session 3.2).
 *
 * Chrome mirrors Visual Ref #1 (`VersionDiffModal.tsx:196-209`)
 * COMPOSITIONALLY (same backdrop + dialog structure, no shared import):
 * - Backdrop: `fixed inset-0 bg-void/95 backdrop-blur-sm z-50 flex items-center justify-center p-6`
 * - Dialog:   `role="dialog" aria-modal="true" aria-labelledby` +
 *             `bg-panel-bg border border-panel-border rounded-[3px] shadow-xl w-full max-w-4xl max-h-[88vh] overflow-hidden flex flex-col`
 * - Uses KEBAB tokens (`bg-panel-bg`/`border-panel-border`) per divergence #5.
 *
 * Diff-render shape: `buildUnifiedRows` / `groupBySection` / `resolveSectionOrder`
 * from `VersionDiffModal.tsx:36-95` reused COMPOSITIONALLY (same logic, NOT
 * a shared import). Driven by `useVersionDiff` (Session 2).
 *
 * Open-gate split mirrors `VersionDiffModal.tsx:161-164`: outer wrapper
 * early-returns null when closed; inner `<BrowseVersionsModalBody>` owns
 * the TanStack Query calls so QueryClient is only required on the open path.
 *
 * Esc-keydown closes via `useEffect` (mirrors `VersionDiffModal.tsx:178-184`).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { ApiError } from '@/lib/api';
import { formatDate, formatPercent, formatMultiple } from '@/lib/format';
import {
  useConversationVersions,
  useVersionDiff,
} from '@/hooks/use-conversation-versions';
import type { ConversationVersionItem } from '@/hooks/use-conversation-versions';
import { RestoreVersionConfirm } from './RestoreVersionConfirm';
import type { VersionDiffResponse } from '@arcanum-hq/shared-types';

// ----------------------------------------------------------------------------
// Diff-render shape (reused compositionally from VersionDiffModal.tsx:36-95)
// ----------------------------------------------------------------------------

const UNCATEGORIZED = 'Other';

interface UnifiedRow {
  name: string;
  category: string;
  displayName?: string;
  oldValue: unknown;
  newValue: unknown;
  oldSource?: string;
  newSource?: string;
  status: 'changed' | 'added' | 'removed';
}

function buildUnifiedRows(inputs: VersionDiffResponse['inputs']): UnifiedRow[] {
  const rows: UnifiedRow[] = [];
  for (const c of inputs.changed) {
    rows.push({
      name: c.name,
      category: c.category ?? UNCATEGORIZED,
      displayName: c.displayName,
      oldValue: c.oldValue,
      newValue: c.newValue,
      oldSource: c.oldSource,
      newSource: c.newSource,
      status: 'changed',
    });
  }
  for (const a of inputs.added) {
    rows.push({
      name: a.name,
      category: a.category ?? UNCATEGORIZED,
      displayName: a.displayName,
      oldValue: undefined,
      newValue: a.value,
      newSource: a.source,
      status: 'added',
    });
  }
  for (const r of inputs.removed) {
    rows.push({
      name: r.name,
      category: r.category ?? UNCATEGORIZED,
      displayName: r.displayName,
      oldValue: r.value,
      newValue: undefined,
      oldSource: r.source,
      status: 'removed',
    });
  }
  rows.sort((a, b) => a.name.localeCompare(b.name));
  return rows;
}

function groupBySection(rows: UnifiedRow[]): Map<string, UnifiedRow[]> {
  const buckets = new Map<string, UnifiedRow[]>();
  for (const row of rows) {
    const bucket = buckets.get(row.category);
    if (bucket) bucket.push(row);
    else buckets.set(row.category, [row]);
  }
  return buckets;
}

/**
 * Section render order: schema-supplied `categoryOrder` first, then any
 * categories present in rows but missing from the order list (alphabetical),
 * then the `UNCATEGORIZED` bucket last.
 */
function resolveSectionOrder(
  buckets: Map<string, UnifiedRow[]>,
  schemaOrder: string[] | undefined
): string[] {
  const present = new Set(buckets.keys());
  const ordered: string[] = [];
  const seen = new Set<string>();
  for (const cat of schemaOrder ?? []) {
    if (present.has(cat) && !seen.has(cat)) {
      ordered.push(cat);
      seen.add(cat);
    }
  }
  const remaining = [...present]
    .filter((c) => !seen.has(c) && c !== UNCATEGORIZED)
    .sort();
  ordered.push(...remaining);
  if (present.has(UNCATEGORIZED)) ordered.push(UNCATEGORIZED);
  return ordered;
}

/**
 * Format a raw metric value for display. Mirrors `VersionDiffModal`'s
 * `formatValue` local helper.
 */
function formatMetricValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v.length === 0 ? '—' : v;
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return '—';
    return String(v);
  }
  return String(v);
}

// ----------------------------------------------------------------------------
// Frozen-metric display helpers (per row in the flat list)
// ----------------------------------------------------------------------------

/**
 * Extract a display string for a frozen metric key from a version's metrics
 * blob. Returns `'—'` when absent or the server already returns the em-dash.
 *
 * DIV-8: the version-list wire is NUMBER-ONLY + FRACTION-SCALE.
 * `VersionListItemSchema.metrics = z.record(z.string(), z.number())` — both the
 * freeze-at-create path and the ADR-0006 list-read projection filter to finite
 * numbers. IRR/CoC are frozen as raw fractions (0.1859) → `formatPercent` (×100).
 * DSCR/EM are bare multiples (1.35) → `formatMultiple`. The string branch is a
 * dead-code safety net (never reached in production).
 */

// Percent (fraction-in ×100) keys — DIV-8: wire metrics are raw FRACTION floats.
// `formatPercent` is correct; `formatPercentRaw` would yield 0.2% instead of 18.6%.
const PERCENT_KEYS = new Set(['IRR', 'StabilizedCoC', 'NOIMargin']);
// Bare-multiple keys (e.g. 1.35 → "1.35x").
const MULTIPLE_KEYS = new Set(['StabilizedDSCR', 'EquityMultiple']);

function extractMetric(
  metrics: ConversationVersionItem['metrics'],
  key: string
): string {
  const raw = metrics[key];
  if (raw === null || raw === undefined) return '—';
  if (typeof raw === 'string') return raw.length > 0 ? raw : '—';
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return '—';
    if (PERCENT_KEYS.has(key)) return formatPercent(raw);
    if (MULTIPLE_KEYS.has(key)) return formatMultiple(raw);
    return String(raw); // fallback: future numeric key not yet mapped
  }
  return '—';
}

// ----------------------------------------------------------------------------
// Props
// ----------------------------------------------------------------------------

export interface BrowseVersionsModalProps {
  /**
   * The conversation ID to which this modal is bound. The listener will
   * ignore any `arcanum:open-versions` events that carry a different id.
   */
  conversationId: string | null;
  /**
   * The conversation's `currentVersionId` pointer — rows matching this id
   * receive a "CURRENT" prefix indicator.
   */
  currentVersionId?: string | null;
  /**
   * Whether the current model state is version-ready (completeness gate).
   * Threaded down to `<RestoreVersionConfirm>` for the "save first" button.
   * ADR 0028: passed from the route which already computes this.
   */
  versionReady?: boolean;
  /**
   * Disabled-tooltip copy for the "create version first" button inside
   * `<RestoreVersionConfirm>`. Built from the route's `versionReadiness`
   * unaddressed list (same as ManageVersionsButton).
   */
  blockingReason?: string;
}

// ----------------------------------------------------------------------------
// Window-event type
// ----------------------------------------------------------------------------

interface OpenVersionsDetail {
  conversationId: string;
}

// ----------------------------------------------------------------------------
// Outer wrapper (open-gate split — mirrors VersionDiffModal.tsx:161-164)
// ----------------------------------------------------------------------------

export function BrowseVersionsModal({
  conversationId,
  currentVersionId,
  versionReady = false,
  blockingReason = '',
}: BrowseVersionsModalProps) {
  const [open, setOpen] = useState(false);

  const onClose = useCallback(() => setOpen(false), []);

  // Root-level window event listener — mirrors <SendDealLinkModal> pattern.
  // Conversation-id-match guard prevents cross-route firing.
  useEffect(() => {
    function handleEvent(e: Event) {
      if (!(e instanceof CustomEvent)) return;
      if (typeof window === 'undefined') return;
      const detail = e.detail as OpenVersionsDetail | undefined;
      if (!detail?.conversationId) return;
      // Guard: only open when the event matches THIS modal's conversation.
      if (detail.conversationId !== conversationId) return;
      setOpen(true);
    }
    window.addEventListener('arcanum:open-versions', handleEvent);
    return () =>
      window.removeEventListener('arcanum:open-versions', handleEvent);
  }, [conversationId]);

  if (!open) return null;

  return (
    <BrowseVersionsModalBody
      conversationId={conversationId}
      currentVersionId={currentVersionId}
      versionReady={versionReady}
      blockingReason={blockingReason}
      onClose={onClose}
    />
  );
}

// ----------------------------------------------------------------------------
// Inner body (owns all useQuery calls — scoped to the open path)
// ----------------------------------------------------------------------------

interface BodyProps {
  conversationId: string | null;
  currentVersionId?: string | null;
  versionReady: boolean;
  blockingReason: string;
  onClose: () => void;
}

type DiffSelection = {
  from: string;
  to: string;
  fromLabel: string;
  toLabel: string;
};

function BrowseVersionsModalBody({
  conversationId,
  currentVersionId,
  versionReady,
  blockingReason,
  onClose,
}: BodyProps) {
  // Esc closes — mirrors VersionDiffModal.tsx:178-184.
  useEffect(() => {
    function onKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeydown);
    return () => window.removeEventListener('keydown', onKeydown);
  }, [onClose]);

  // Inline diff: track which two versions are selected for comparison.
  const [diffSel, setDiffSel] = useState<DiffSelection | null>(null);
  // Track the two selected version ids (for the select-two UI).
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Restore confirm — opened from a per-row Restore button (Session 4).
  const [restoreTarget, setRestoreTarget] = useState<{
    id: string;
    label: string;
  } | null>(null);

  const versionsQuery = useConversationVersions(conversationId);

  // Diff query — enabled only when two refs are selected.
  const diffQuery = useVersionDiff(
    conversationId,
    diffSel?.from ?? null,
    diffSel?.to ?? 'live'
  );

  // Handle selecting/deselecting a version row for diffing.
  const handleToggleSelect = useCallback((version: ConversationVersionItem) => {
    setSelectedIds((prev) => {
      if (prev.includes(version.id)) {
        // Deselect
        const next = prev.filter((id) => id !== version.id);
        if (next.length < 2) setDiffSel(null);
        return next;
      }
      if (prev.length >= 2) {
        // Replace oldest selection
        const next = [prev[prev.length - 1]!, version.id];
        // Identify from/to refs from the versions list (using id as ref).
        setDiffSel({
          from: prev[prev.length - 1]!,
          to: version.id,
          fromLabel: prev[prev.length - 1]!,
          toLabel: version.id,
        });
        return next;
      }
      const next = [...prev, version.id];
      if (next.length === 2) {
        setDiffSel({
          from: next[0]!,
          to: next[1]!,
          fromLabel: next[0]!,
          toLabel: next[1]!,
        });
      }
      return next;
    });
  }, []);

  // Update diff selection labels once versions are loaded.
  const versionsData = versionsQuery.data;
  const diffSelRef = useRef(diffSel);
  diffSelRef.current = diffSel;

  useEffect(() => {
    if (!versionsData || !diffSelRef.current) return;
    const { from, to } = diffSelRef.current;
    const fromVersion = versionsData.find((v) => v.id === from);
    const toVersion = versionsData.find((v) => v.id === to);
    if (fromVersion && toVersion) {
      setDiffSel({
        from,
        to,
        fromLabel: fromVersion.label,
        toLabel: toVersion.label,
      });
    }
  }, [versionsData]);

  return (
    <div
      className="fixed inset-0 bg-void/95 backdrop-blur-sm z-50 flex items-center justify-center p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="browse-versions-modal-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="browse-versions-modal-title"
        data-testid="browse-versions-modal"
        className="bg-panel-bg border border-panel-border rounded-[3px] shadow-xl w-full max-w-4xl max-h-[88vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-panel-border gap-4">
          <div className="flex flex-col gap-1 min-w-0">
            <h2
              id="browse-versions-modal-title"
              className="font-mono text-xs text-text-bright uppercase tracking-[1px]"
            >
              Versions
            </h2>
            {diffSel && versionsData && (
              <p className="text-xs font-mono text-text-muted truncate">
                <span>{diffSel.fromLabel}</span>
                <span className="mx-2 text-text-dim">→</span>
                <span>{diffSel.toLabel}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {diffSel && (
              <Button
                variant="secondary"
                onClick={() => {
                  setDiffSel(null);
                  setSelectedIds([]);
                }}
                className="text-micro"
                data-testid="browse-versions-clear-diff"
              >
                Clear diff
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={onClose}
              className="text-micro"
            >
              Close
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {versionsQuery.isLoading ? (
            <div
              data-testid="browse-versions-loading"
              className="flex flex-col gap-3 px-5 py-4"
            >
              <Skeleton variant="block" className="h-12" />
              <Skeleton variant="block" className="h-12" />
              <Skeleton variant="block" className="h-12" />
            </div>
          ) : versionsQuery.isError ? (
            <div className="px-5 py-4">
              <EmptyState
                title={
                  versionsQuery.error instanceof ApiError &&
                  versionsQuery.error.status === 404
                    ? 'VERSIONS NOT FOUND'
                    : "COULDN'T LOAD VERSIONS"
                }
                description={
                  versionsQuery.error instanceof ApiError &&
                  versionsQuery.error.status === 404
                    ? "This conversation doesn't exist or you don't have access."
                    : 'Something went wrong fetching versions. Try again.'
                }
                action={
                  <Button
                    variant="primary"
                    onClick={() => versionsQuery.refetch()}
                  >
                    Retry
                  </Button>
                }
              />
            </div>
          ) : !versionsData || versionsData.length === 0 ? (
            <div className="px-5 py-4">
              <EmptyState
                title="NO VERSIONS YET"
                description="Create a version from the Variables panel to save a snapshot of your model inputs."
              />
            </div>
          ) : (
            <>
              {/* Flat ordered list — newest first */}
              <div className="px-5 py-4">
                {/* Select-two hint */}
                {selectedIds.length < 2 && (
                  <p
                    className="font-mono text-micro text-text-dim mb-3"
                    data-testid="browse-versions-select-hint"
                  >
                    {selectedIds.length === 0
                      ? 'Select two versions to compare.'
                      : 'Select one more version to compare.'}
                  </p>
                )}

                {/* Column headers */}
                <div
                  className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_auto_auto] gap-4 border-b border-panel-border pb-2 mb-2"
                  data-testid="browse-versions-column-headers"
                >
                  <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px]">
                    Version
                  </span>
                  <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px]">
                    Created
                  </span>
                  <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px] tabular-nums">
                    IRR
                  </span>
                  <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px] tabular-nums">
                    DSCR
                  </span>
                  <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px]">
                    Market
                  </span>
                  <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px] w-16 text-right">
                    Compare
                  </span>
                  <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px] w-16 text-right">
                    Restore
                  </span>
                </div>

                {/* Version rows */}
                <div
                  className="flex flex-col"
                  data-testid="browse-versions-list"
                >
                  {versionsData.map((version) => {
                    const isCurrent = version.id === currentVersionId;
                    const isSelected = selectedIds.includes(version.id);
                    const irr = extractMetric(version.metrics, 'IRR');
                    const dscr = extractMetric(
                      version.metrics,
                      'StabilizedDSCR'
                    );
                    const market = extractMetric(version.metrics, 'market');

                    return (
                      <div
                        key={version.id}
                        data-testid={`browse-versions-row-${version.id}`}
                        data-current={isCurrent ? 'true' : undefined}
                        data-selected={isSelected ? 'true' : undefined}
                        className={clsx(
                          'grid grid-cols-[2fr_1fr_1fr_1fr_1fr_auto_auto] gap-4 py-3 border-t border-panel-border/40 first:border-t-0 items-center',
                          isSelected && 'bg-panel-border/20'
                        )}
                      >
                        {/* Label + CURRENT indicator */}
                        <div className="flex flex-col gap-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {isCurrent && (
                              <span
                                className="inline-flex items-center gap-1 font-mono text-micro text-accent uppercase tracking-[0.5px]"
                                data-testid={`browse-versions-current-${version.id}`}
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                                CURRENT
                              </span>
                            )}
                            <span
                              className={clsx(
                                'font-mono text-xs truncate',
                                isCurrent ? 'text-text-bright' : 'text-text'
                              )}
                            >
                              {version.label}
                            </span>
                          </div>
                          {/* staleVsCurrentModel badge */}
                          {version.staleVsCurrentModel && (
                            <span
                              className="font-mono text-micro text-warn"
                              data-testid={`browse-versions-stale-badge-${version.id}`}
                            >
                              computed against an earlier model
                            </span>
                          )}
                        </div>

                        {/* Created at */}
                        <span className="font-mono text-xs text-text-muted tabular-nums">
                          {formatDate(version.created_at)}
                        </span>

                        {/* IRR */}
                        <span className="font-mono text-xs text-text tabular-nums">
                          {irr}
                        </span>

                        {/* DSCR */}
                        <span className="font-mono text-xs text-text tabular-nums">
                          {dscr}
                        </span>

                        {/* Market */}
                        <span className="font-mono text-xs text-text truncate">
                          {market}
                        </span>

                        {/* Select for comparison */}
                        <div className="flex justify-end w-16">
                          <button
                            type="button"
                            onClick={() => handleToggleSelect(version)}
                            data-testid={`browse-versions-select-${version.id}`}
                            className={clsx(
                              'font-mono text-micro uppercase tracking-[0.5px] px-2 py-1 border rounded-[2px] transition-colors',
                              isSelected
                                ? 'text-accent border-accent/40 bg-accent/7'
                                : 'text-text-muted border-panel-border hover:text-text hover:border-panel-border-hi'
                            )}
                          >
                            {isSelected ? 'Selected' : 'Select'}
                          </button>
                        </div>

                        {/* Restore — opens RestoreVersionConfirm (Session 4) */}
                        <div className="flex justify-end w-16">
                          <button
                            type="button"
                            onClick={() =>
                              setRestoreTarget({
                                id: version.id,
                                label: version.label,
                              })
                            }
                            data-testid={`browse-versions-restore-${version.id}`}
                            disabled={isCurrent}
                            className={clsx(
                              'font-mono text-micro uppercase tracking-[0.5px] px-2 py-1 border rounded-[2px] transition-colors',
                              isCurrent
                                ? 'text-text-dim border-panel-border cursor-not-allowed opacity-40'
                                : 'text-text-muted border-panel-border hover:text-warn hover:border-warn/40'
                            )}
                            title={
                              isCurrent
                                ? 'This is already the current version'
                                : `Restore to ${version.label}`
                            }
                          >
                            Restore
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Inline diff panel — rendered when two versions are selected */}
              {diffSel && (
                <div
                  className="border-t border-panel-border"
                  data-testid="browse-versions-diff-panel"
                >
                  {diffQuery.isLoading ? (
                    <div
                      className="flex flex-col gap-3 px-5 py-4"
                      data-testid="browse-versions-diff-loading"
                    >
                      <Skeleton variant="block" className="h-12" />
                      <Skeleton variant="block" className="h-32" />
                    </div>
                  ) : diffQuery.isError ? (
                    <div className="px-5 py-4">
                      <EmptyState
                        title="COULDN'T LOAD DIFF"
                        description="Something went wrong fetching the diff. Try again."
                        action={
                          <Button
                            variant="primary"
                            onClick={() => diffQuery.refetch()}
                          >
                            Retry
                          </Button>
                        }
                      />
                    </div>
                  ) : diffQuery.data ? (
                    <DiffPanel data={diffQuery.data} />
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* RestoreVersionConfirm — opened from per-row Restore buttons (Session 4).
          Mounted inside the body so it shares the same query-client scope.
          ADR 0028: versionReady + blockingReason threaded for the "save first"
          escape hatch; onRestoreSuccess closes BOTH the confirm and browse
          modals so the user returns directly to the builder. */}
      <RestoreVersionConfirm
        open={restoreTarget !== null}
        onClose={() => setRestoreTarget(null)}
        onRestoreSuccess={() => {
          setRestoreTarget(null);
          onClose();
        }}
        conversationId={conversationId}
        versionId={restoreTarget?.id ?? null}
        versionLabel={restoreTarget?.label ?? ''}
        versionReady={versionReady}
        blockingReason={blockingReason}
      />
    </div>
  );
}

// ----------------------------------------------------------------------------
// DiffPanel — renders the VersionDiffResponse via the reused render-shape
// (same buildUnifiedRows/groupBySection/resolveSectionOrder compositional logic
// as VersionDiffModal.tsx:36-95; NOT a shared import)
// ----------------------------------------------------------------------------

interface DiffPanelProps {
  data: VersionDiffResponse;
}

function DiffPanel({ data }: DiffPanelProps) {
  const { inputs, metrics = [], categoryOrder } = data;
  const rows = buildUnifiedRows(inputs);
  const grouped = groupBySection(rows);
  const populatedSections = resolveSectionOrder(grouped, categoryOrder);

  return (
    <div className="px-5 py-4" data-testid="browse-versions-diff-content">
      {/* Key metrics strip */}
      {metrics.length > 0 && (
        <div className="mb-5" data-testid="browse-versions-diff-metrics">
          <h3 className="font-mono text-micro text-text-muted uppercase tracking-[0.5px] mb-3">
            Key metrics
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {metrics.map((m) => {
              const finite = Number.isFinite(m.deltaPct);
              const tone =
                !finite || m.deltaPct === 0
                  ? 'text-text-muted'
                  : m.deltaPct > 0
                  ? 'text-accent'
                  : 'text-warn';
              return (
                <div
                  key={m.name}
                  data-testid={`browse-versions-diff-metric-${m.name}`}
                  className="border border-panel-border rounded-[2px] bg-panel-bg p-3 flex flex-col gap-1"
                >
                  <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px]">
                    {m.displayName ?? m.name}
                  </span>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-mono text-xs text-text-muted tabular-nums">
                      {formatMetricValue(m.oldValue)}
                    </span>
                    <span className="text-text-dim">→</span>
                    <span className="font-mono text-sm text-text-bright tabular-nums">
                      {formatMetricValue(m.newValue)}
                    </span>
                  </div>
                  <span className={`font-mono text-micro tabular-nums ${tone}`}>
                    {Number.isFinite(m.deltaPct)
                      ? `${m.deltaPct >= 0 ? '+' : ''}${m.deltaPct.toFixed(1)}%`
                      : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Input diff rows */}
      <div data-testid="browse-versions-diff-inputs">
        <h3 className="font-mono text-micro text-text-muted uppercase tracking-[0.5px] mb-3">
          Input changes
        </h3>

        {/* Column headers */}
        <div
          className="grid grid-cols-[2fr_1.5fr_1.5fr_1fr] gap-4 border-b border-panel-border pb-2 mb-3 sticky top-0 bg-panel-bg z-10"
          data-testid="browse-versions-diff-col-headers"
        >
          <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px]">
            Field
          </span>
          <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px]">
            Before
          </span>
          <span className="font-mono text-micro text-text-bright uppercase tracking-[0.5px]">
            After
          </span>
          <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px]">
            Source
          </span>
        </div>

        {populatedSections.length === 0 ? (
          <EmptyState
            title="NO INPUT CHANGES"
            description="These versions have the same inputs."
          />
        ) : (
          populatedSections.map((section) => {
            const sectionRows = grouped.get(section) ?? [];
            const slug = section
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-|-$/g, '');
            return (
              <div
                key={section}
                data-testid={`browse-versions-diff-section-${slug}`}
                className="mb-5 last:mb-0"
              >
                <h4 className="font-mono text-micro text-accent uppercase tracking-[1px] mb-2">
                  {section}
                  <span className="ml-2 text-text-dim">
                    ({sectionRows.length})
                  </span>
                </h4>
                <div className="flex flex-col">
                  {sectionRows.map((row) => (
                    <div
                      key={row.name}
                      data-testid={`browse-versions-diff-row-${row.name}`}
                      data-row-status={row.status}
                      className="grid grid-cols-[2fr_1.5fr_1.5fr_1fr] gap-4 py-2 border-t border-panel-border/40 first:border-t-0 items-baseline"
                    >
                      <span
                        className="font-mono text-xs text-text-bright break-all"
                        title={row.name}
                      >
                        {row.displayName ?? row.name}
                      </span>
                      <span
                        className={clsx(
                          'font-mono text-xs tabular-nums break-all',
                          row.status === 'added'
                            ? 'text-text-dim'
                            : row.status === 'removed'
                            ? 'text-warn line-through'
                            : 'text-text-muted'
                        )}
                      >
                        {formatMetricValue(row.oldValue)}
                      </span>
                      <span
                        className={clsx(
                          'font-mono text-xs tabular-nums break-all',
                          row.status === 'removed'
                            ? 'text-text-dim'
                            : row.status === 'added'
                            ? 'text-accent'
                            : 'text-text-bright'
                        )}
                      >
                        {formatMetricValue(row.newValue)}
                      </span>
                      <span className="font-mono text-micro text-text-muted">
                        {row.oldSource &&
                        row.newSource &&
                        row.oldSource !== row.newSource ? (
                          <>
                            <span className="text-text-dim">
                              {row.oldSource}
                            </span>
                            <span className="mx-1 text-text-dim">→</span>
                            <span>{row.newSource}</span>
                          </>
                        ) : (
                          <span className="text-text-dim">
                            {row.newSource ?? row.oldSource ?? '—'}
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
